import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme as useMuiTheme,
  Backdrop,
  Alert,
  alpha,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  Route as RouteIcon,
  Satellite as SatelliteIcon,
  DirectionsCar as VehicleIcon,
  KeyboardArrowUp as ChevronUpIcon,
  KeyboardArrowRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import XLSX from "sheetjs-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from "../utlis/axiosInstance";
import moment from "moment";
import { useTheme } from "../utlis/useTheme";
import DeviceSearchSelect, {
  DeviceOption,
} from "../components/DeviceSearchSelect";

interface ReportData {
  id: number;
  terminal_id: string;
  protocol: string;
  created_at: number;
  date_time: string;
  fix_time: number;
  latitude: number;
  longitude: number;
  altitude: number | null;
  speed: number;
  course: number;
  status: {
    gps_positioned: boolean;
    east_longitude: boolean;
    north_latitude: boolean;
    gps_real_time: boolean;
  };
  attributes: Record<string, any>;
  geofence_ids: (number | null)[];
  device_id: number;
  address: string | null;
  alarm: any[];
}

const formatDate = (timestamp: number | string) => {
  if (!timestamp) return "";

  let date: Date;

  if (typeof timestamp === "number") {
    // Handle Unix timestamp (could be in seconds or milliseconds)
    if (timestamp > 1000000000000) {
      // Timestamp is in milliseconds
      date = new Date(timestamp);
    } else {
      // Timestamp is in seconds
      date = new Date(timestamp * 1000);
    }
  } else {
    // Handle date string
    date = new Date(timestamp);
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  return format(date, "dd/MM/yyyy HH:mm:ss");
};

const RouteReport: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } =
    useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>(
    id ? [{ id, name: id }] : [],
  );
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Today 12:00 AM
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleGenerateReport = async () => {
    if (!selectedDevices.length) {
      setError("Please select at least one device.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Match mobile app time calculation: subtract 5 hours 30 minutes (UTC to IST offset)
      const fromDateStr = moment(fromDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DD HH:mm:ss");

      const toDateStr = moment(toDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DD HH:mm:ss");

      // Create device name mapping
      const deviceIdToName: Record<string, string> = Object.fromEntries(
        selectedDevices.map((device) => [device.id, device.name || device.id]),
      );

      const selectedDeviceIds = selectedDevices.map((device) => device.id);

      // Fetch all selected devices in parallel
      const responses = await Promise.all(
        selectedDeviceIds.map((deviceId) =>
          axiosInstance
            .get(
              `/gpstracker/positionhandler/?from=${fromDateStr}&to=${toDateStr}&device_id=${deviceId}`,
            )
            .then((response) => ({ response, deviceId })),
        ),
      );

      const allPositions: ReportData[] = [];
      for (const { response, deviceId } of responses) {
        if (response.data?.status) {
          const positions = response.data?.responseData?.positions || [];
          // Add device info to each position
          const positionsWithDevice = positions.map((pos: any) => ({
            ...pos,
            device_id: deviceId,
            device_name: deviceIdToName[deviceId] || deviceId,
          }));
          allPositions.push(...positionsWithDevice);
        }
      }

      // Sort by fix_time (most recent first)
      allPositions.sort((a, b) => {
        const timeA = a.fix_time || a.created_at || 0;
        const timeB = b.fix_time || b.created_at || 0;
        return timeB - timeA;
      });

      setReportData(allPositions);
      setPage(0);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Error generating report. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      // Create header rows like your CSV format
      const headerRows = [
        [
          "From DateTime",
          "",
          moment(fromDate).format("DD/MM/YYYY HH:mm:ss"),
          "",
          "",
          "",
          "",
        ],
        [
          "To DateTime",
          "",
          moment(toDate).format("DD/MM/YYYY HH:mm:ss"),
          "",
          "",
          "",
          "",
        ],
        [
          "Vehicle No",
          "",
          selectedDevices.map((device) => device.name || device.id).join(", "),
          "",
          "",
          "",
          "",
          "",
        ],
        [
          "Total Positions",
          "",
          reportData.length.toString(),
          "",
          "",
          "",
          "",
          "",
        ],
        ["", "", "", "", "", "", "", ""], // Empty row
        [
          "Sr.",
          "Vehicle No.",
          "Speed (Km/h)",
          "Motion",
          "Address",
          "GPS Time",
          "Server Time",
          "Internal Battery",
        ],
      ];

      // Create data rows
      const dataRows = reportData.map((row, index) => [
        index + 1,
        (row as any).device_name || row.device_id,
        row.speed.toFixed(2),
        row.speed > 0 ? "Moving" : "Stopped",
        row.address || "N/A",
        formatDate(new Date(row.date_time).toISOString()),
        formatDate(row.created_at),
        `${row.attributes?.gps_battery || 0}%`,
      ]);

      // Combine header and data rows
      const allRows = [...headerRows, ...dataRows];

      setDownloadProgress(50);

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Position Report");

      setDownloadProgress(75);

      XLSX.writeFile(
        workbook,
        `Position_Report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`,
      );

      setDownloadProgress(100);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  const handleExportPDF = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const doc = new jsPDF("landscape");

      doc.setFontSize(14);
      doc.text("Position Report", 14, 16);
      doc.setFontSize(10);
      doc.text(
        `From: ${moment(fromDate).format("DD/MM/YYYY HH:mm:ss")}`,
        14,
        24,
      );
      doc.text(`To: ${moment(toDate).format("DD/MM/YYYY HH:mm:ss")}`, 110, 24);
      doc.text(`Total Positions: ${reportData.length}`, 14, 30);
      doc.text(
        `Devices: ${selectedDevices.map((device) => device.name || device.id).join(", ")}`,
        110,
        30,
      );

      const columns = [
        "Sr.",
        "Vehicle No.",
        "Speed (Km/h)",
        "Motion",
        "Address",
        "GPS Time",
        "Server Time",
        "Internal Battery",
      ];

      const rows = reportData.map((row, index) => [
        index + 1,
        (row as any).device_name || row.device_id,
        row.speed.toFixed(2),
        row.speed > 0 ? "Moving" : "Stopped",
        row.address || "N/A",
        formatDate(new Date(row.date_time).toISOString()),
        formatDate(row.created_at),
        `${row.attributes?.gps_battery || 0}%`,
      ]);

      setDownloadProgress(30);

      // Use autotable plugin
      // @ts-ignore
      (autoTable as any)(doc, {
        head: [columns],
        body: rows,
        startY: 36,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [230, 236, 255] },
        theme: "grid",
        didDrawPage: (dataArg: any) => {
          // page footer
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height
            ? pageSize.height
            : (pageSize as any).getHeight();
          doc.setFontSize(9);
          const pageNumber = doc.getNumberOfPages();
          doc.text(
            `Page ${pageNumber}`,
            doc.internal.pageSize.getWidth() - 30,
            pageHeight - 10,
          );
        },
      });

      setDownloadProgress(80);

      doc.save(`Position_Report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.pdf`);

      setDownloadProgress(100);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const currentData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // CSS styles for the component
  const styles = `
    .route-report-container {
      font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      background-color: #f8fafc;
      min-height: 100vh;
    }

    .header-section {
      background-color: white;
      border-bottom: 1px solid #e2e8f0;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .header-content {
      padding: 1.5rem 1.5rem;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .back-button {
      color: #6b7280;
      transition: color 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.5rem;
      transition: all 0.2s ease;
    }

    .back-button:hover {
      color: #374151;
      background-color: #f3f4f6;
    }

    .back-icon {
      width: 1.5rem;
      height: 1.5rem;
    }

    .page-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }

    .error-alert {
      margin: 1rem 1.5rem;
    }

    .main-content {
      padding: 1.5rem;
    }

    .filter-panel {
      background-color: white;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .filter-grid {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: nowrap;
      justify-content: flex-start;
    }

    .filter-item {
      display: flex;
      flex-direction: column;
      min-width: 160px;
      max-width: 200px;
      flex: 0 0 auto;
    }

    .filter-label {
      display: block;
      font-size: 0.75rem;
      font-weight: 500;
      color: #374151;
      margin-bottom: 0.25rem;
    }

    .date-input-container {
      position: relative;
    }

    .date-input {
      width: 100%;
   
      font-size: 0.875rem;
      transition: all 0.2s ease;
    }

    .date-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .select-container {
      position: relative;
    }

    .device-select {
      width: 100%;
    }

    .device-option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
    }

    .device-icon {
      color: #6b7280;
      font-size: 1.25rem;
    }

    .device-name {
      font-size: 0.875rem;
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
      margin-left: auto;
    }

    .action-buttons {
      display: flex;
      gap: 0.375rem;
    }

    .generate-btn {
      background-color: var(--primary-color) !important;
      color: white !important;
      padding: 0.4rem 0.8rem !important;
      border-radius: 0.25rem !important;
      font-weight: 500 !important;
      text-transform: none !important;
      font-size: 0.7rem !important;
      transition: all 0.2s ease !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
      min-width: 75px;
      height: 32px;
    }

    .generate-btn:hover {
      background-color: var(--primary-light) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
    }

    .export-btn {
      padding: 0.4rem 0.8rem !important;
      border-radius: 0.25rem !important;
      font-weight: 500 !important;
      text-transform: none !important;
      font-size: 0.7rem !important;
      transition: all 0.2s ease !important;
      min-width: 65px;
      height: 32px;
      border-width: 1px !important;
    }

    .excel-btn {
      color: #059669 !important;
      border-color: #059669 !important;
    }

    .excel-btn:hover {
      background-color: #059669 !important;
      color: white !important;
    }

    .pdf-btn {
      color: #dc2626 !important;
      border-color: #dc2626 !important;
    }

    .pdf-btn:hover {
      background-color: #dc2626 !important;
      color: white !important;
    }

    .table-container {
      background-color: white;
      border-radius: 0.75rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      overflow: hidden;
    }

    .table-wrapper {
      overflow-x: auto;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .table-header {
      background: linear-gradient(to right, #eff6ff, #e0e7ff);
      border-bottom: 1px solid #e5e7eb;
    }

    .table-header-cell {
      padding: 1rem 1.5rem;
      text-align: left;
      font-size: 0.75rem;
      font-weight: 600;
      color: #4b5563;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .expand-header {
      width: 3rem;
      padding: 1rem 0.75rem;
    }

    .table-body {
      background-color: white;
    }

    .table-row {
      border-bottom: 1px solid #f3f4f6;
      transition: all 0.15s ease;
      cursor: pointer;
    }

    .table-row:hover {
      background-color: #eff6ff;
    }

    .row-even {
      background-color: white;
    }

    .row-odd {
      background-color: #f9fafb;
    }

    .row-expanded {
      background-color: #eff6ff;
    }

    .expand-cell {
      padding: 1rem 0.75rem;
      width: 3rem;
    }

    .expand-icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .expand-icon {
      width: 1rem;
      height: 1rem;
      color: #6b7280;
      transition: transform 0.2s ease;
    }

    .sr-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .sr-badge {
      display: flex;
      align-items: center;
    }

    .sr-number {
      width: 2rem;
      height: 2rem;
      background-color: #dbeafe;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: 600;
      color: #2563eb;
    }

    .speed-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .speed-value {
      font-size: 0.875rem;
      font-weight: 500;
      color: #1f2937;
    }

    .ignition-cell,
    .motion-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .status-chip {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .chip {
      display: inline-flex;
      align-items: center;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid;
    }

    .chip-success {
      background-color: #dcfce7;
      color: #166534;
      border-color: #bbf7d0;
    }

    .chip-error {
      background-color: #fee2e2;
      color: #991b1b;
      border-color: #fecaca;
    }

    .chip-primary {
      background-color: #dbeafe;
      color: #1e40af;
      border-color: #bfdbfe;
    }

    .chip-default {
      background-color: #f3f4f6;
      color: #374151;
      border-color: #e5e7eb;
    }

    .address-cell {
      padding: 1rem 1.5rem;
    }

    .address-text {
      font-size: 0.875rem;
      color: #1f2937;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .time-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .time-text {
      font-size: 0.875rem;
      color: #1f2937;
    }

    .battery-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .battery-container {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .battery-bar {
      width: 4rem;
      height: 0.5rem;
      background-color: #e5e7eb;
      border-radius: 9999px;
      overflow: hidden;
    }

    .battery-fill {
      height: 100%;
      border-radius: 9999px;
      transition: width 0.3s ease;
    }

    .battery-high {
      background-color: #10b981;
    }

    .battery-medium {
      background-color: #f59e0b;
    }

    .battery-low {
      background-color: #ef4444;
    }

    .battery-text {
      font-size: 0.875rem;
      font-weight: 500;
      color: #1f2937;
    }

    .expanded-row {
      background: linear-gradient(to bottom right, #f9fafb, #f3f4f6);
    }

    .expanded-content {
      padding: 1.5rem 2rem;
    }

    .detail-card {
      background-color: white;
      border-radius: 0.75rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      overflow: hidden;
    }

    .detail-header {
      background: linear-gradient(to right, #eff6ff, #e0e7ff);
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .detail-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0;
    }

    .detail-body {
      padding: 1.5rem;
    }

    .detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
    }

    .detail-section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .detail-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .detail-value {
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }

    .detail-code {
      font-size: 0.875rem;
      font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
      color: #1f2937;
      background-color: #f9fafb;
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      border: 1px solid #e5e7eb;
    }

    .detail-status {
      display: flex;
      align-items: center;
    }

    .pagination-container {
      background-color: #f9fafb;
      padding: 1rem 1.5rem;
      border-top: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .pagination-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .rows-text {
      font-size: 0.875rem;
      color: #374151;
    }

    .rows-select {
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      padding: 0.25rem 0.75rem;
      font-size: 0.875rem;
      background-color: white;
      color: #374151;
      transition: all 0.2s ease;
    }

    .rows-select:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .pagination-right {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .pagination-info {
      font-size: 0.875rem;
      color: #374151;
    }

    .pagination-buttons {
      display: flex;
      gap: 0.25rem;
    }

    .pagination-btn {
      padding: 0.5rem 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      background-color: white;
      color: #374151;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .pagination-btn:hover:not(:disabled) {
      background-color: white;
      border-color: #9ca3af;
    }

    .pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 768px) {
      .filter-grid {
        flex-direction: column;
        align-items: stretch;
      }

      .filter-item {
        min-width: auto;
      }

      .action-buttons {
        flex-direction: column;
        width: 100%;
      }

      .detail-grid {
        grid-template-columns: 1fr;
      }

      .pagination-container {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
      }

      .pagination-right {
        justify-content: space-between;
      }
    }

    @media (max-width: 640px) {
      .main-content {
        padding: 1rem;
      }

      .filter-panel {
        padding: 1rem;
      }

      .table-header-cell,
      .sr-cell,
      .speed-cell,
      .ignition-cell,
      .motion-cell,
      .address-cell,
      .time-cell,
      .battery-cell {
        padding: 0.75rem;
      }

      .expanded-content {
        padding: 1rem;
      }

      .detail-body {
        padding: 1rem;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="route-report-container">
        {/* Header */}
        <div className="header-section">
          <div className="header-content">
            <div className="header-left">
              <Link to="/reports" className="back-button">
                <ArrowBackIcon className="back-icon" />
              </Link>
              <h1 className="page-title">Position Report</h1>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-alert">
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        <div className="main-content">
          {/* Filter Panel */}
          <div className="filter-panel">
            <div className="filter-grid">
              <div className="filter-item">
                <label className="filter-label">From Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    value={fromDate}
                    onChange={(newValue) => newValue && setFromDate(newValue)}
                    maxDateTime={toDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        className: "date-input",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="filter-item">
                <label className="filter-label">To Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    value={toDate}
                    onChange={(newValue) => newValue && setToDate(newValue)}
                    minDateTime={fromDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        className: "date-input",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div
                className="filter-item"
                style={{ minWidth: "200px", maxWidth: "300px" }}
              >
                <label className="filter-label">Select Devices</label>
                <DeviceSearchSelect
                  label="Add Device"
                  multiple
                  value={selectedDevices}
                  onChange={(newValue) =>
                    setSelectedDevices((newValue as DeviceOption[]) || [])
                  }
                  maxSelection={10}
                  onMaxSelectionReached={() =>
                    setError("Maximum 10 devices can be selected.")
                  }
                />
                {/* Selected Devices Summary */}
                {selectedDevices.length > 0 && (
                  <Box sx={{ marginTop: "0.5rem" }}>
                    {selectedDevices.length > 50 ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#e3f2fd",
                          border: "1px solid #90caf9",
                          borderRadius: "8px",
                          padding: "0.5rem 0.75rem",
                          gap: "0.5rem",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#0284F5",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          {`${selectedDevices.length.toLocaleString()} Devices Selected`}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSelectedDevices([])}
                          sx={{
                            minWidth: "auto",
                            padding: "0.25rem 0.5rem",
                            color: "#0284F5",
                            fontSize: "0.7rem",
                            textTransform: "none",
                            border: "1px solid #0284F5",
                            "&:hover": {
                              backgroundColor: "rgba(2, 132, 245, 0.1)",
                            },
                          }}
                          variant="outlined"
                        >
                          Clear All
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        {selectedDevices.map((device) => {
                          return (
                            <Box
                              key={device.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#e3f2fd",
                                border: "1px solid #90caf9",
                                borderRadius: "16px",
                                padding: "0.25rem 0.5rem",
                                gap: "0.25rem",
                              }}
                            >
                              <Typography
                                sx={{ color: "#0284F5", fontSize: "0.75rem" }}
                              >
                                {device.name || device.id}
                              </Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  setSelectedDevices((prev) =>
                                    prev.filter(
                                      (selected) => selected.id !== device.id,
                                    ),
                                  )
                                }
                                sx={{
                                  minWidth: "auto",
                                  padding: "0",
                                  color: "#0284F5",
                                  "&:hover": {
                                    backgroundColor: "rgba(2, 132, 245, 0.1)",
                                  },
                                }}
                              >
                                ×
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </div>

              <div className="filter-actions">
                <div className="action-buttons">
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleGenerateReport}
                    disabled={loading || !selectedDevices.length}
                    className="generate-btn"
                    sx={{
                      bgcolor: getPrimaryColor() || "#1D2B56",
                      "&:hover": {
                        bgcolor: getPrimaryLightColor(),
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Generate"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={downloading || reportData.length === 0}
                    className="export-btn excel-btn"
                  >
                    {downloading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Excel"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={handleExportPDF}
                    disabled={reportData.length === 0}
                    className="export-btn pdf-btn"
                  >
                    PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            <div className="table-wrapper">
              <table className="data-table">
                <thead className="table-header">
                  <tr>
                    <th className="expand-header"></th>
                    <th className="table-header-cell">Sr.</th>
                    <th className="table-header-cell">Vehicle No.</th>
                    <th className="table-header-cell">Speed (Km/h)</th>
                    <th className="table-header-cell">Motion</th>
                    <th className="table-header-cell">Address</th>
                    <th className="table-header-cell">GPS Time</th>
                    <th className="table-header-cell">Server Time</th>
                    <th className="table-header-cell">Internal Battery</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {currentData.map((row, index) => {
                    const isExpanded = expandedRows.includes(row.id);
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className={`table-row ${index % 2 === 0 ? "row-even" : "row-odd"} ${isExpanded ? "row-expanded" : ""}`}
                          onClick={() => toggleRow(row.id)}
                        >
                          <td className="expand-cell">
                            <div className="expand-icon-container">
                              {isExpanded ? (
                                <ChevronUpIcon className="expand-icon" />
                              ) : (
                                <ChevronRightIcon className="expand-icon" />
                              )}
                            </div>
                          </td>
                          <td className="sr-cell">
                            <div className="sr-badge">
                              <span className="sr-number">
                                {page * rowsPerPage + index + 1}
                              </span>
                            </div>
                          </td>
                          <td className="device-cell">
                            <span className="device-text">
                              {(row as any).device_name || row.device_id}
                            </span>
                          </td>
                          <td className="speed-cell">
                            <span className="speed-value">
                              {row.speed.toFixed(2)}
                            </span>
                          </td>
                          {/* <td className="ignition-cell">
                            <div className="status-chip">
                              {row.attributes?.ignition ? (
                                <span className="chip chip-success">ON</span>
                              ) : (
                                <span className="chip chip-error">OFF</span>
                              )}
                            </div>
                          </td> */}
                          <td className="motion-cell">
                            <div className="status-chip">
                              {row.speed > 0 ? (
                                <span className="chip chip-primary">
                                  Moving
                                </span>
                              ) : (
                                <span className="chip chip-default">
                                  Stopped
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="address-cell">
                            <span className="address-text">
                              {row.address || "N/A"}
                            </span>
                          </td>
                          <td className="time-cell">
                            <span className="time-text">
                              {formatDate(
                                new Date(row.date_time).toISOString(),
                              )}
                            </span>
                          </td>

                          <td className="time-cell">
                            <span className="time-text">
                              {formatDate(row.created_at)}
                            </span>
                          </td>
                          <td className="battery-cell">
                            <div className="battery-container">
                              <div className="battery-bar">
                                <div
                                  className={`battery-fill ${
                                    row.attributes?.gps_battery > 70
                                      ? "battery-high"
                                      : row.attributes?.gps_battery > 40
                                        ? "battery-medium"
                                        : "battery-low"
                                  }`}
                                  style={{
                                    width: `${row.attributes?.gps_battery || 0}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="battery-text">
                                {row.attributes?.gps_battery || 0}%
                              </span>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-row">
                            <td colSpan={10} className="expanded-content">
                              <div className="detail-card">
                                <div className="detail-header">
                                  <h4 className="detail-title">
                                    Detailed Information
                                  </h4>
                                </div>
                                <div className="detail-body">
                                  <div className="detail-grid">
                                    {/* Distance */}
                                    <div className="detail-section">
                                      <span className="detail-label">
                                        Total Distance
                                      </span>
                                      <div className="detail-value">
                                        {row.attributes?.distance?.toFixed(1) ||
                                          "0.0"}{" "}
                                        km
                                      </div>
                                      <span className="detail-label">
                                        Distance From Last
                                      </span>
                                      <div className="detail-value">
                                        {row.attributes?.distance_from_last?.toFixed(
                                          1,
                                        ) || "0.0"}{" "}
                                        km
                                      </div>
                                    </div>

                                    {/* Location */}
                                    <div className="detail-section">
                                      <span className="detail-label">
                                        Coordinates
                                      </span>
                                      <div className="detail-code">{`${row.latitude?.toFixed(6)}, ${row.longitude?.toFixed(6)}`}</div>
                                      <span className="detail-label">
                                        Address
                                      </span>
                                      <div className="detail-code">
                                        {row.address || "N/A"}
                                      </div>
                                    </div>

                                    {/* Time */}
                                    <div className="detail-section">
                                      <span className="detail-label">
                                        GPS Time
                                      </span>
                                      <div className="detail-code">
                                        {formatDate(row.created_at)}
                                      </div>
                                      <span className="detail-label">
                                        Server Time
                                      </span>
                                      <div className="detail-code">
                                        {formatDate(row.created_at)}
                                      </div>
                                      <span className="detail-label">
                                        Device Time
                                      </span>
                                      <div className="detail-code">
                                        {formatDate(
                                          new Date(row.date_time).toISOString(),
                                        )}
                                      </div>
                                    </div>

                                    {/* System */}
                                    <div className="detail-section">
                                      <span className="detail-label">
                                        External Power
                                      </span>
                                      <div className="detail-status">
                                        {row.attributes?.external_power ? (
                                          <span className="chip chip-success">
                                            Connected
                                          </span>
                                        ) : (
                                          <span className="chip chip-error">
                                            Disconnected
                                          </span>
                                        )}
                                      </div>
                                      <span className="detail-label">
                                        Temperature
                                      </span>
                                      <div className="detail-value">
                                        {row.attributes?.temperature
                                          ? `${row.attributes.temperature}°C`
                                          : "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
              <div className="pagination-left">
                <span className="rows-text">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="rows-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="pagination-right">
                <span className="pagination-info">
                  {page * rowsPerPage + 1}-
                  {Math.min((page + 1) * rowsPerPage, reportData.length)} of{" "}
                  {reportData.length}
                </span>
                <div className="pagination-buttons">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="pagination-btn"
                  >
                    ←
                  </button>
                  <button
                    onClick={() =>
                      setPage(
                        Math.min(
                          Math.ceil(reportData.length / rowsPerPage) - 1,
                          page + 1,
                        ),
                      )
                    }
                    disabled={
                      page >= Math.ceil(reportData.length / rowsPerPage) - 1
                    }
                    className="pagination-btn"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download Progress Dialog */}
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={downloading}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={60}
              thickness={4}
              sx={{ mb: 2 }}
            />
            <Typography variant="h6" color="text.primary">
              {downloadProgress < 100
                ? "Preparing Download..."
                : "Download Complete!"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {downloadProgress}% Complete
            </Typography>
          </Box>
        </Backdrop>
      </div>
    </>
  );
};

export default RouteReport;
